/**
 * Design-token registry (B1).
 *
 * The CSS in src/app/globals.css is the RENDERING source of truth; this
 * module is its typed mirror for JS consumers (tests, inline styles,
 * chart palettes). tokens.test.ts asserts the two never drift: every value
 * here must appear verbatim as `--c-<key>: <value>` in globals.css.
 *
 * Two brands (ZW default, VIP) × two themes (light/dark, `.dark` class).
 * ZW is a dark-only brand — the source SPA (zoomwarriors-frontend) declares
 * exactly one palette — so its light and dark palettes are identical by
 * design. Provenance and derivation notes live next to the CSS palettes.
 */

export type BrandId = "zw" | "vip";
export type ThemeId = "light" | "dark";

/** Attribute on <html> that selects the brand palette in globals.css. */
export const BRAND_ATTRIBUTE = "data-brand";

export const DEFAULT_BRAND: BrandId = "zw";

export const BRANDS: readonly BrandId[] = ["zw", "vip"] as const;

/** Suffixes of the `--c-*` custom properties every brand palette defines. */
export const TOKEN_KEYS = [
  "page",
  "shell",
  "panel",
  "surface",
  "elevated",
  "edge",
  "ink",
  "ink-light",
  "ink-faint",
  "accent",
  "accent-hover",
  "accent-cta",
  "accent-cta-hover",
  "blue-pale",
  "orange-pale",
  "gold",
  "subtle",
  "on-accent",
  "link",
] as const;

export type TokenKey = (typeof TOKEN_KEYS)[number];

export type TokenPalette = Record<TokenKey, string>;

const ZW_PALETTE: TokenPalette = {
  page: "#0a0a0a",
  shell: "#1a1a1a",
  panel: "#1a1a1a",
  surface: "#1a1a1a",
  elevated: "#2a2a2a",
  edge: "#343a40",
  ink: "#ffffff",
  "ink-light": "#f8f9fa",
  "ink-faint": "#adb5bd",
  accent: "#2563eb",
  "accent-hover": "#1d4ed8",
  "accent-cta": "#2563eb",
  "accent-cta-hover": "#1d4ed8",
  "blue-pale": "rgba(37, 99, 235, 0.15)",
  "orange-pale": "rgba(202, 138, 4, 0.15)",
  gold: "#60a5fa",
  subtle: "#6c757d",
  "on-accent": "#ffffff",
  link: "#60a5fa",
};

const VIP_LIGHT: TokenPalette = {
  page: "#f5f5f7",
  shell: "#f7f7f7",
  panel: "#f7f7f7",
  surface: "#ffffff",
  elevated: "#f3f3f5",
  edge: "#d6d6da",
  ink: "#1A1A1A",
  "ink-light": "#5b5a54",
  "ink-faint": "#74736e",
  accent: "#44689d",
  "accent-hover": "#365985",
  "accent-cta": "#D85A30",
  "accent-cta-hover": "#c04e28",
  "blue-pale": "#E6F1FB",
  "orange-pale": "#FAECE7",
  gold: "#e9b85a",
  subtle: "#888780",
  "on-accent": "#ffffff",
  link: "#44689d",
};

const VIP_DARK: TokenPalette = {
  page: "#09090b",
  shell: "#121215",
  panel: "#121215",
  surface: "#18181b",
  elevated: "#26262c",
  edge: "#4b4b54",
  ink: "#ffffff",
  "ink-light": "#a0aec0",
  "ink-faint": "#94a3b8",
  accent: "#85B7EB",
  "accent-hover": "#112353",
  "accent-cta": "#F0997B",
  "accent-cta-hover": "#D85A30",
  "blue-pale": "rgba(133, 183, 235, 0.15)",
  "orange-pale": "rgba(240, 153, 123, 0.15)",
  gold: "#fbd38a",
  subtle: "#64748b",
  "on-accent": "#09090b",
  link: "#85B7EB",
};

export const BRAND_TOKENS: Record<BrandId, Record<ThemeId, TokenPalette>> = {
  // ZW: one palette for both themes (dark-only brand, see module docblock).
  zw: { light: ZW_PALETTE, dark: ZW_PALETTE },
  vip: { light: VIP_LIGHT, dark: VIP_DARK },
};

/** Per-brand typeface, as declared on `--brand-font` in globals.css. */
export const BRAND_FONTS: Record<BrandId, string> = {
  zw: "var(--font-inter)",
  vip: "var(--font-dm-sans)",
};

/** Corner radii registered as ds-radius-* classes in globals.css. */
export const RADII = {
  card: "0.75rem",
  widget: "1.6rem",
  pill: "999px",
  dialog: "28px",
} as const;

/**
 * The palette as CSS custom-property declarations, e.g.
 * `{ "--c-page": "#0a0a0a", … }`. Useful for inline-style theming in tests
 * and tools; production styling reads the cascade from globals.css.
 */
export function tokenVariables(
  brand: BrandId,
  theme: ThemeId,
): Record<string, string> {
  const palette = BRAND_TOKENS[brand][theme];
  return Object.fromEntries(
    TOKEN_KEYS.map((key) => [`--c-${key}`, palette[key]]),
  );
}

/** Read the active brand off an element (usually document.documentElement). */
export function currentBrand(root: Element): BrandId {
  const value = root.getAttribute(BRAND_ATTRIBUTE);
  return value === "vip" || value === "zw" ? value : DEFAULT_BRAND;
}

/** Switch the active brand by flipping the data-brand attribute. */
export function applyBrand(root: Element, brand: BrandId): void {
  root.setAttribute(BRAND_ATTRIBUTE, brand);
}
